/**
 * Wires the desktop shell's tray, language, push-to-talk hook and updater to
 * the client (docs/build/desktop-bridge.md). Does nothing on the web.
 *
 * - Tray "Mute microphone" works like the dock's mute button: it is checked
 *   while the mic is off (muted or deafened), and choosing it toggles the
 *   same way (unmuting while deafened undeafens). The check mark shows the
 *   client's real state (honest UI).
 * - The UI language → the tray menu's language.
 * - The shell's global key hook follows the input mode (voice/controller).
 * - The updater: a downloaded, verified update waits for "Restart to update"
 *   (`useDesktopUpdate`, shown by shell/Update.tsx).
 */
import type { DesktopUpdateStatus } from '@neosocial/protocol';
import { create } from 'zustand';
import { syncShellPttKey, toggleMute } from '../voice/controller';
import { useVoice } from '../voice/store';
import type { VoiceSession } from '../voice/machine';
import { desktopBridge, isMobileApp } from './config';
import { connectGameDetection } from '../social/activity';

/** The mic is off for the tray as for the dock: self-muted or deafened. */
function micOff(s: VoiceSession): boolean {
  return s.selfMute || s.selfDeaf;
}

interface DesktopUpdateState {
  /** The shell's updater, as last reported (null on the web or before the first answer). */
  status: DesktopUpdateStatus | null;
  /** `installUpdate()` is running. */
  installing: boolean;
  /** The last install attempt failed. */
  failed: boolean;
}

export const useDesktopUpdate = create<DesktopUpdateState>(() => ({
  status: null,
  installing: false,
  failed: false,
}));

/** Installs the downloaded update; the shell restarts the app when it works. */
export async function installDesktopUpdate(): Promise<void> {
  const bridge = desktopBridge();
  if (!bridge || useDesktopUpdate.getState().installing) return;
  useDesktopUpdate.setState({ installing: true, failed: false });
  try {
    await bridge.installUpdate();
  } catch (e) {
    console.warn('the desktop update could not be installed:', e);
    useDesktopUpdate.setState({ failed: true });
  } finally {
    useDesktopUpdate.setState({ installing: false });
  }
}

/** The calls the frameless window's caption buttons need (Tauri's window API). */
export interface DesktopWindow {
  minimize(): Promise<void>;
  toggleMaximize(): Promise<void>;
  close(): Promise<void>;
  isMaximized(): Promise<boolean>;
  onResized(cb: () => void): Promise<() => void>;
  setDecorations?(on: boolean): Promise<void>;
  /** Flashes the taskbar button (Tauri's `UserAttentionType`: 1 Critical, 2 Informational; null stops). */
  requestUserAttention?(type: 1 | 2 | null): Promise<void>;
}

/**
 * The desktop shell's own window (`withGlobalTauri`), or null on the web. The
 * window is frameless (tauri.conf.json `decorations: false`), so the title
 * bar draws the caption buttons; close hides to the tray, as the native
 * button did (lib.rs `CloseRequested`). The phone app has Tauri's window
 * API too, but no window to drag or caption: null there.
 */
export function desktopWindow(): DesktopWindow | null {
  if (isMobileApp()) return null;
  const tauri = (
    globalThis as { __TAURI__?: { window?: { getCurrentWindow?: () => DesktopWindow } } }
  ).__TAURI__;
  try {
    return tauri?.window?.getCurrentWindow?.() ?? null;
  } catch {
    return null;
  }
}

export function connectDesktopShell(): void {
  const bridge = desktopBridge();
  if (!bridge) return;
  const quiet = (p: Promise<unknown>) => p.catch(() => undefined);

  // the app is English only: a shell that remembered another tray language switches back
  quiet(bridge.setLocale('en'));

  // frameless (the title bar draws the caption buttons): the window-state
  // plugin may bring back the native frame an older version saved
  const win = desktopWindow();
  if (win?.setDecorations) quiet(win.setDecorations(false));

  // The tray asks for `muted`; act only when that differs from what the
  // user sees, with the dock's own toggle (QA DT-BUG-05).
  bridge.onTrayMute((muted) => {
    if (muted !== micOff(useVoice.getState().session)) toggleMute();
  });
  let lastMuted = micOff(useVoice.getState().session);
  quiet(bridge.setTrayMuted(lastMuted));
  useVoice.subscribe((s) => {
    const now = micOff(s.session);
    if (now !== lastMuted) {
      lastMuted = now;
      quiet(bridge.setTrayMuted(now));
    }
  });

  // The shell re-installs the key it saved at every launch: drop it when
  // this client is on voice activation (QA DT-BUG-07).
  syncShellPttKey();

  // Rich presence (2026-10-29): the shell's game detection, reported when shared.
  connectGameDetection();

  // QA DT-BUG-02: nothing read the updater, so a ready update never installed.
  const take = (status: DesktopUpdateStatus) => useDesktopUpdate.setState({ status });
  bridge.onUpdate(take);
  bridge.getUpdateStatus().then(
    (s) => {
      // an event that arrived meanwhile is newer than this answer
      if (!useDesktopUpdate.getState().status) take(s);
    },
    () => undefined,
  );
}
